/**
 * React Query hook for fetching batch analytics data
 *
 * Provides analytics data for the Analytics tab including:
 * - Jobs by state distribution
 * - Parse and run success rates
 * - Execution time distribution
 * - Completion timeline
 * - Top errors
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { batchService } from '../services/api';
import { BatchAnalytics } from '../types/batch';

const BATCH_ANALYTICS_QUERY_KEY = 'batch-analytics';

/**
 * Hook to fetch analytics data for a specific batch
 */
export function useBatchAnalytics(
  batchId: string | undefined,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<BatchAnalytics, Error> {
  return useQuery({
    queryKey: [BATCH_ANALYTICS_QUERY_KEY, batchId],
    queryFn: () => {
      if (!batchId) {
        throw new Error('Batch ID is required');
      }
      return batchService.getAnalytics(batchId);
    },
    enabled: !!batchId && (options?.enabled !== false),
    staleTime: 60000, // 1 minute cache - analytics are expensive to compute
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}
